import { useState, useEffect, useRef, useCallback } from "react";

export const STORAGE_WAKE_LOCK_ENABLED = "bible_wake_lock_enabled";

export const isWakeLockSupported = () => {
  return Boolean(
    typeof navigator !== "undefined" &&
      navigator.wakeLock &&
      typeof navigator.wakeLock.request === "function"
  );
};

export const getWakeLockPref = () => {
  try {
    if (typeof window === "undefined" || !window.localStorage) return true;
    const val = window.localStorage.getItem(STORAGE_WAKE_LOCK_ENABLED);
    if (val === null) return true;
    return val === "true";
  } catch (e) {
    return true;
  }
};

export const setWakeLockPref = (enabled) => {
  try {
    if (typeof window !== "undefined" && window.localStorage) {
      window.localStorage.setItem(
        STORAGE_WAKE_LOCK_ENABLED,
        String(Boolean(enabled))
      );
      window.dispatchEvent(
        new CustomEvent("bible_wake_lock_changed", {
          detail: { enabled: Boolean(enabled) },
        })
      );
    }
  } catch (e) {}
};

export const useWakeLock = ({ autoRequest = true } = {}) => {
  const isSupported = isWakeLockSupported();
  const [isEnabled, setIsEnabledState] = useState(getWakeLockPref);
  const [isActive, setIsActive] = useState(false);
  const sentinelRef = useRef(null);

  const requestLock = useCallback(async () => {
    if (!isWakeLockSupported()) return false;
    if (
      typeof document !== "undefined" &&
      document.visibilityState !== "visible"
    ) {
      return false;
    }
    if (sentinelRef.current && !sentinelRef.current.released) {
      setIsActive(true);
      return true;
    }
    try {
      const sentinel = await navigator.wakeLock.request("screen");
      sentinelRef.current = sentinel;
      setIsActive(true);

      sentinel.addEventListener("release", () => {
        if (sentinelRef.current === sentinel) {
          sentinelRef.current = null;
        }
        setIsActive(false);
      });
      return true;
    } catch (err) {
      setIsActive(false);
      return false;
    }
  }, []);

  const releaseLock = useCallback(async () => {
    try {
      if (sentinelRef.current && !sentinelRef.current.released) {
        await sentinelRef.current.release();
      }
    } catch (err) {
      // Ignore release errors
    } finally {
      sentinelRef.current = null;
      setIsActive(false);
    }
  }, []);

  const toggleEnabled = useCallback(() => {
    const next = !isEnabled;
    setIsEnabledState(next);
    setWakeLockPref(next);
  }, [isEnabled]);

  const setIsEnabled = useCallback((nextVal) => {
    const next = Boolean(nextVal);
    setIsEnabledState(next);
    setWakeLockPref(next);
  }, []);

  // Sync preference with external changes (e.g. SettingsPanel)
  useEffect(() => {
    const handleSync = () => {
      setIsEnabledState(getWakeLockPref());
    };
    if (typeof window !== "undefined") {
      window.addEventListener("bible_wake_lock_changed", handleSync);
      window.addEventListener("storage", handleSync);
      return () => {
        window.removeEventListener("bible_wake_lock_changed", handleSync);
        window.removeEventListener("storage", handleSync);
      };
    }
  }, []);

  // Manage lock lifecycle based on preference and autoRequest
  useEffect(() => {
    if (!isSupported) return;

    if (isEnabled && autoRequest) {
      requestLock();
    } else {
      releaseLock();
    }

    return () => {
      releaseLock();
    };
  }, [isEnabled, autoRequest, isSupported, requestLock, releaseLock]);

  // Re-acquire lock when user switches back to the tab
  useEffect(() => {
    if (!isSupported) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && isEnabled && autoRequest) {
        requestLock();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [isEnabled, autoRequest, isSupported, requestLock]);

  return {
    isSupported,
    isEnabled,
    isActive,
    requestLock,
    releaseLock,
    toggleEnabled,
    setIsEnabled,
  };
};

export default useWakeLock;
